import { CycleCalendar } from "@/components/cycle/cycle-calendar";
import { CycleComparisonTable } from "@/components/cycle/cycle-comparison-table";
import { StageTimeline } from "@/components/cycle/stage-timeline";
import { RouteShell } from "@/components/route-shell";
import { trpc } from "@/lib/trpc";
import type { CycleStageStatus } from "@payrollmart-invoice/shared";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowRight, Zap } from "lucide-react";

export const Route = createFileRoute("/_app/cycle")({
	component: CyclePageRoute,
});

function CyclePageRoute() {
	return (
		<RouteShell>
			<CyclePage />
		</RouteShell>
	);
}

function CyclePage() {
	const { data: cycle, isLoading: cycleLoading } = trpc.invoices.currentCycle.useQuery(undefined, {
		throwOnError: true,
	});
	const { data: history, isLoading: historyLoading } = trpc.invoices.cycleHistory.useQuery(
		{ months: 4 },
		{ throwOnError: true },
	);

	const headline = cycle
		? `${cycle.monthLabel} cycle — day ${cycle.dayOfCycle} of ${cycle.totalDays}`
		: "Billing Cycle";
	const sub = cycle ? buildSubCopy(cycle.stages, cycle.dayOfCycle, cycle.totalDays) : null;

	return (
		<div className="space-y-6" data-testid="cycle-page">
			{/* Page header */}
			<div className="flex flex-wrap items-start justify-between gap-4">
				<div>
					<h1 className="text-xl font-semibold text-[#1e1e20]" data-testid="cycle-page-title">
						{headline}
					</h1>
					{sub && <p className="mt-1 max-w-2xl text-sm text-[#6b7280]">{sub}</p>}
				</div>
				<Link
					to="/invoices/review"
					className="inline-flex items-center gap-1.5 rounded-md bg-[#4f46e5] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4338ca]"
				>
					<Zap className="h-4 w-4" strokeWidth={1.5} />
					Continue reviewing
					<ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
				</Link>
			</div>

			{/* Calendar + Today */}
			<div className="grid gap-4 lg:grid-cols-[1.3fr_1fr]">
				{cycleLoading || !cycle ? <CalendarSkeleton /> : <CycleCalendar cycle={cycle} />}
				{cycle && <TodayCard cycle={cycle} />}
			</div>

			{/* Stage timeline */}
			{cycleLoading || !cycle ? <StageTimelineSkeleton /> : <StageTimeline stages={cycle.stages} />}

			{/* Cycle-vs-cycle comparison */}
			{historyLoading || !history ? (
				<ComparisonSkeleton />
			) : (
				<CycleComparisonTable rows={history} />
			)}
		</div>
	);
}

function TodayCard({
	cycle,
}: {
	cycle: { monthLabel: string; dayOfCycle: number; stages: CycleStageStatus[] };
}) {
	const activeStage = cycle.stages.find((s) => s.state === "active");
	const focus = activeStage ?? cycle.stages[cycle.stages.length - 1];
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-6" data-testid="cycle-today-card">
			<div className="flex items-center gap-2">
				<Zap className="h-4 w-4 text-[#4f46e5]" strokeWidth={1.5} />
				<h2 className="text-sm font-semibold text-[#1e1e20]">Today · day {cycle.dayOfCycle}</h2>
			</div>
			{focus ? (
				<>
					<p className="mt-4 text-[17px] font-medium leading-snug text-[#1e1e20]">
						{focus.primary}
					</p>
					<p className="mt-3 text-sm leading-relaxed text-[#6b7280]">{focus.secondary}</p>
					{focus.cta && (
						<a
							href={focus.cta.href}
							className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-[#4f46e5] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4338ca]"
						>
							{focus.cta.label}
							<ArrowRight className="h-4 w-4" strokeWidth={2} />
						</a>
					)}
				</>
			) : (
				<p className="mt-4 text-sm text-[#6b7280]">Nothing pressing — all caught up.</p>
			)}
		</div>
	);
}

/**
 * Small helper to pick the best single-sentence summary for the page header.
 * Prefers the active stage; falls back to whichever stage is next in the
 * canonical Upload → Extract → Review → Push order.
 */
function buildSubCopy(stages: CycleStageStatus[], dayOfCycle: number, totalDays: number): string {
	const active = stages.find((s) => s.state === "active");
	if (active) return `${active.primary}. ${active.secondary}`;
	const nextWaiting = stages.find((s) => s.state === "waiting");
	if (nextWaiting) return nextWaiting.secondary;
	return `Day ${dayOfCycle} of ${totalDays}. Every stage is complete.`;
}

// ---- Skeleton placeholders -----------------------------------------------

const CALENDAR_SKELETON_KEYS = Array.from({ length: 28 }, (_, i) => `cal-${i}`);
const STAGE_SKELETON_KEYS = ["upload", "extract", "review", "push"];
const COMPARISON_SKELETON_KEYS = ["row-0", "row-1", "row-2", "row-3"];

function CalendarSkeleton() {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-4" data-testid="cycle-skeleton">
			<div className="h-6 w-40 animate-pulse rounded bg-[#f3f4f6]" />
			<div className="mt-4 grid grid-cols-7 gap-1">
				{CALENDAR_SKELETON_KEYS.map((key) => (
					<div
						key={key}
						className="aspect-square animate-pulse rounded-md border border-[#e5e7eb] bg-[#f8f9fb]"
					/>
				))}
			</div>
		</div>
	);
}

function StageTimelineSkeleton() {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
			{STAGE_SKELETON_KEYS.map((key) => (
				<div key={key} className="h-16 animate-pulse border-b border-[#f3f4f6] last:border-b-0" />
			))}
		</div>
	);
}

function ComparisonSkeleton() {
	return (
		<div className="rounded-lg border border-[#e5e7eb] bg-white p-6">
			<div className="h-5 w-48 animate-pulse rounded bg-[#f3f4f6]" />
			<div className="mt-4 space-y-2">
				{COMPARISON_SKELETON_KEYS.map((key) => (
					<div key={key} className="h-10 animate-pulse rounded bg-[#f8f9fb]" />
				))}
			</div>
		</div>
	);
}
